// src/components/templates/TemplateSelector.jsx
import React from 'react';
import { Book, User, Users, ShoppingBag, Store, Folder, File, Cog2, Clock, Car, Building2, Image, 
  Calendar, School, ShoppingCart, Bed, Briefcase, Building, BookOpen, GraduationCap, Brain, Search 
} from 'lucide-react';

const templates = [
  { 
    id: 'education', 
    name: 'Education', 
    icon: Book, 
    description: 'Interactive learning materials with quizzes and progress tracking',
    features: ['Math rendering', 'Quizzes', 'Progress tracking', 'Student responses'],
    color: 'blue'
  },
  { 
    id: 'marketing', 
    name: 'Marketing', 
    icon: Users, 
    description: 'Engaging marketing campaigns with lead capture',
    features: ['Lead capture', 'Analytics', 'Social sharing', 'Call-to-actions'],
    color: 'green'
  },
  { 
    id: 'brochure', 
    name: 'Brochure', 
    icon: Briefcase, 
    description: 'Professional brochures and product catalogs',
    features: ['Product galleries', 'Contact forms', 'Download links', 'Company info'],
    color: 'purple'
  },
  { 
    id: 'catalog', 
    name: 'Catalog', 
    icon: Store, 
    description: 'Product catalogs with filtering, search, and item management',
    features: ['Item management', 'Grid customization', 'Modal popups', 'External links', 'Contact integration'],
    color: 'orange'
  },
  { 
    id: 'album', 
    name: 'Album', 
    icon: Image, 
    description: 'Photo and video galleries with slideshow modes',
    features: ['Photo galleries', 'Video players', 'Slideshow modes', 'Media organization'],
    color: 'pink'
  },
  { 
    id: 'ecommerce', 
    name: 'eCommerce', 
    icon: ShoppingCart, 
    description: 'Complete online store with shopping cart and payment processing',
    features: ['Product management', 'Shopping cart', 'Order processing', 'Payment integration', 'Analytics dashboard'],
    color: 'emerald'
  },

  { 
    id: 'institution', 
    name: 'Institution', 
    icon: School, 
    description: 'Comprehensive websites for schools, colleges, and educational institutions',
    features: ['Staff directory', 'Event calendar', 'Resource management', 'Student portal', 'Admissions'],
    color: 'indigo'
  },

  { 
    id: 'organisation', 
    name: 'Organisation', 
    icon: Building, 
    description: 'Complete organisational websites with management features',
    features: ['Team management', 'Asset tracking', 'Event management', 'Interactive features', 'Multiple organisation types'],
    color: 'indigo'
  },
  {
  id: 'profile',
    name: 'Profile Template', 
    description: 'Create professional profiles for individuals, organizations, projects, and assets',
    icon: User,
    category: 'profile',
    color: 'bg-purple-500',
    features: [
      'Multiple Profile Types',
      'Interactive Sections',
      'Contact Integration', 
      'Project Showcases',
      'Achievement Tracking',
      'Social Media Links'
    ],
    subTypes: [
      { id: 'personal', name: 'Personal Profile', icon: '👤' },
      { id: 'organization', name: 'Organization Profile', icon: '🏢' },
      { id: 'project', name: 'Project Profile', icon: '📁' },
      { id: 'asset', name: 'Asset Profile', icon: '📦' }
    ],
    isPopular: true
  },

  { 
    id: 'events', 
    name: 'Events', 
    icon: Calendar, 
    description: 'Complete event management with booking, galleries, and supplier coordination',
    features: ['Event booking', 'Public/private sections', 'Payment integration', 'Check-ins', 'Gift registries', 'Supplier management', 'Event galleries'],
    color: 'emerald'
  },
  {
  id: 'booking', 
    name: 'Booking', 
    icon: Calendar, 
    description: 'Comprehensive booking system for accommodations, appointments, rentals, and venues',
    features: [
      'Multi-vendor support', 
      'Calendar management', 
      'Real-time availability', 
      'Payment integration',
      'Guest management',
      'Rating & reviews',
      'Automated emails'
    ],
    color: 'emerald',
    bookingTypes: [
      { type: 'accommodation', label: 'Hotels & Stays', icon: Bed },
      { type: 'appointment', label: 'Appointments', icon: Clock },
      { type: 'rental', label: 'Rentals', icon: Car },
      { type: 'venue', label: 'Venues & Events', icon: Building2 }
    ]
  },

  {
    id: 'flipbook',
    name: 'Flipbook',
    description: 'Interactive flipbook with page narration and PDF import',
    icon: BookOpen,
    color: 'from-indigo-500 to-purple-600',
    features: [
      'PDF to flipbook conversion',
      'Page-by-page narration',
      'Realistic page turning',
      'Audio recording per page',
      'Mobile responsive'
    ],
    recommended: 'Books, magazines, catalogs, presentations'
  },

  {
    id: 'leitner',
    name: 'Leitner System',
    description: 'Spaced repetition learning system with flashcards and progress tracking',
    icon: Brain,
    color: 'from-indigo-500 to-purple-600',
    features: [
      '5-box Leitner system',
      'Flashcard creation',
      'Custom test builder',
      'Progress tracking',
      'Performance analytics',
      'Timer & sessions'
    ],
    recommended: 'Study materials, exam prep, language learning, skill mastery'
  },

  {
    id: 'websearch',
    name: 'WebSearch',
    description: 'AI-powered lead generation and market research tool',
    icon: Search,
    color: '#4f46e5',
    features: [
      'AI-powered search',
      'Lead generation',
      'Supplier discovery',
      'Tender tracking',
      'Engagement tracking',
      'CSV export'
    ],
    category: 'business',
    popular: true
  },

  {
    id: 'requests',
    name: 'Open Requests',
    description: 'Describe your request and let providers come to you.',
    icon: Search,
    color: '#0795ff',
    features: [
      'AI-powered requests',
      'Provider Offers',
      'Provider Profiles',
      'CSV export'
    ],
    category: 'business',
    popular: true
  },

  {
    id: 'diary',
    name: 'Diary & Planner',
    icon: Calendar,
    description: 'Personal diary with meetings, calendar, journal, and contacts',
    features: ['Meeting management', 'Calendar views', 'Journal entries', 'Contact book', 'AI suggestions', 'Templates'],
    color: 'indigo'
  },

  {
    id: 'course',
    name: 'Course & Learning',
    description: 'Complete learning management system with courses, lessons, and gamification',
    icon: GraduationCap, // Import from lucide-react
    color: 'from-blue-500 to-indigo-600',
    features: [
      'Course creation & management',
      'Interactive lessons with quizzes',
      'Video & multimedia content',
      'Student progress tracking',
      'XP & achievements system',
      'Virtual 1-on-1 sessions',
      'Certificates & badges',
      'Discussion forums'
    ],
    recommended: 'Online courses, training programs, educational content',
    popular: true
  },

  { 
    id: 'general', 
    name: 'General', 
    icon: Book, 
    description: 'Flexible template for any type of interactive content',
    features: ['Custom layouts', 'Mixed content', 'Flexible structure', 'All features'],
    color: 'gray'
  }
];

export const TemplateSelector = ({ onSelect }) => {
  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-blue-200 hover:border-blue-300 hover:bg-blue-50',
      green: 'border-green-200 hover:border-green-300 hover:bg-green-50',
      indigo: 'border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50',
      purple: 'border-purple-200 hover:border-purple-300 hover:bg-purple-50',
      orange: 'border-orange-200 hover:border-orange-300 hover:bg-orange-50',
      emerald: 'border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50',
      pink: 'border-pink-200 hover:border-pink-300 hover:bg-pink-50',
      gray: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
    };
    return colors[color] || colors.gray;
  };

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'text-blue-500 bg-blue-100',
      green: 'text-green-500 bg-green-100',
      purple: 'text-purple-500 bg-purple-100',
      indigo: 'text-indigo-500 bg-indigo-100',
      orange: 'text-orange-500 bg-orange-100',
      pink: 'text-pink-500 bg-pink-100',
      gray: 'text-gray-500 bg-gray-100',
      emerald: 'text-emerald-500 bg-emerald-100'
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {templates.map(template => {
        const IconComponent = template.icon;
        return (
          <div 
            key={template.id}
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all duration-200 bg-white ${getColorClasses(template.color)}`}
            onClick={() => onSelect(template)}
          >
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${getIconColorClasses(template.color)}`}>
              <IconComponent size={24} />
            </div>
            
            <h3 className="text-xl font-semibold mb-2 text-gray-900">
              {template.name}
            </h3>
            
            <p className="text-gray-600 mb-4 text-sm leading-relaxed">
              {template.description}
            </p>
            
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-700 mb-2">Key Features:</p>
              {template.features.map(feature => (
                <div key={feature} className="flex items-center text-xs text-gray-600">
                  <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
                  {feature}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};